import React from "react";
import { FormRendererConfig } from "./types/FormRendererConfig";
import FormContainer from "../index";
import SchemaService from "./SchemaService";
import { MessageIfErrorInForm, ScrollToErrorOnSubmit } from "../util";
import { AnyObject } from "../../../global";
import { FormikProps } from "formik";
import RenderFormFromSchema from "./components/RenderFormFromSchema";
import CaptchaComponent from "../../../apps/registration/components/captcha";
import FieldIdentifier from "./enums/FieldIdentifier";
import { DynamicFormRendererProvider } from "./context/dynamicFormRendererContext";
import { FormDebug } from "shared/form/debug";
import classNames from "classnames";

/**
 * Component to render form dynamically
 * @interface FormRendererConfig
 * @param props
 * @constructor
 * @example ```<DynamicFormRenderer formRef={formRef} schemaList={data} formSubmitHandler={onSubmit} />```
 * You can use any button for submit, but on click of that button call formRef.current.submitForm()
 * to call form submit handler
 */
export default function DynamicFormRenderer(props: FormRendererConfig) {
  const innerRef = React.useRef<any>();
  /**
   * useImperativeHandle is used to modify ref and provide custom methods
   */
  React.useImperativeHandle(props.formRef, () => ({
    submitForm: () => {
      innerRef.current.submitForm();
    },
    resetForm: () => {
      innerRef.current.resetForm();
    },
    dirty: () => innerRef.current.dirty,
    validateForm: () => innerRef.current.validateForm(),
    isValid: () => innerRef.current.isValid,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) =>
      innerRef.current.setFieldValue(field, value, shouldValidate),
    values: () => innerRef.current.values
  }));
  /**
   * effect to let the parent know that form is rendered
   */
  const onRender = props.onRender;
  React.useEffect(() => {
    onRender?.(true);
    return () => onRender?.(false);
  }, [onRender]);

  /**
   * This Submit handler is called when Form submit handler will be triggered programmatically.
   * And values will be parsed against each type of field
   * @return Array<{
   *     id: <string>,
   *     alias: <string>,
   *     system: <boolean>,
   *     value: <type is based of each field type>
   * }>
   */
  const handleFormSubmit = React.useCallback(
    values => {
      const parsedValues = SchemaService.getValuesOnFormSubmit(props.schemaList, values);
      if (props.parseValuesOnSubmitToApiReq) {
        let parsedFormValues = SchemaService.createApiReqObjForSubmit(parsedValues);
        if (values[FieldIdentifier.ORGANIZATION_ID]) {
          parsedFormValues = {
            ...parsedFormValues,
            [FieldIdentifier.ORGANIZATION_ID]: values[FieldIdentifier.ORGANIZATION_ID]
          };
        }
        if (props.includeCaptcha) {
          parsedFormValues = {
            ...parsedFormValues,
            apiCaptcha: {
              [FieldIdentifier.CAPTCHA_TEXT]: values[FieldIdentifier.CAPTCHA_TEXT],
              [FieldIdentifier.CAPTCHA_ID]: values[FieldIdentifier.CAPTCHA_ID]
            }
          };
        }
        props.formSubmitHandler?.(parsedFormValues);
      } else {
        props.formSubmitHandler?.(parsedValues);
      }
    },
    [props]
  );

  const initialFormValues = React.useMemo(() => {
    let initialValues = SchemaService.getInitialValue(props.schemaList);
    if (props.customData?.organizationId) {
      initialValues = {
        ...initialValues,
        [FieldIdentifier.ORGANIZATION_ID]: props.customData.organizationId
      };
    }
    if (props.includeCaptcha) {
      return {
        ...initialValues,
        [FieldIdentifier.CAPTCHA_ID]: "",
        [FieldIdentifier.CAPTCHA_TEXT]: ""
      };
    }
    return initialValues;
  }, [props.schemaList, props.includeCaptcha, props.customData?.organizationId]);

  const renderFormSchema = React.useMemo(() => {
    const formSchemaList = props.schemaList.map(schemaObj => {
      return { ...schemaObj, disabled: schemaObj.disabled || props.disabled };
    });
    return renderForm({ ...props, schemaList: formSchemaList });
  }, [props]);

  return (
    <DynamicFormRendererProvider {...props.customData}>
      {props.schemaList && props.schemaList.length > 0 ? (
        <FormContainer
          innerRef={innerRef}
          onSubmit={handleFormSubmit}
          initialValues={initialFormValues}
          validationSchema={props.validationSchema}
          enableReinitialize={props.enableReinitialize}>
          {renderFormSchema}
        </FormContainer>
      ) : (
        <div>
          <p>Field Configuration Missing.</p>
        </div>
      )}
    </DynamicFormRendererProvider>
  );
}

function renderForm(props: FormRendererConfig) {
  return ({ values }: FormikProps<AnyObject>) => (
    <FormContainer.Form className={classNames("form--flex", props.additionalClass)}>
      {/* TODO: SR rethink this class */}
      <RenderFormFromSchema schemaList={props.schemaList} formValues={values} />
      {props.includeCaptcha && <CaptchaComponent required />}
      <ScrollToErrorOnSubmit />
      <MessageIfErrorInForm />
      <FormDebug />
    </FormContainer.Form>
  );
}
